import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, ArrowRight, Shield, CheckCircle, Award, Users
} from "lucide-react";
import { SEO, StatCard } from "@/components/shared";
import { fadeInUp, staggerContainer, viewportOnce, HOME_STATS, HOME_PRINCIPLES, WHY_CHOOSE_US } from "@/constants";
import { PartnerMarquee } from "@/components/PartnerMarquee";

const U = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600`;
const IMG = {
  hero: U("photo-1758518730083-4c12527b6742"),
  campus: U("photo-1653503425441-9d975e51ce91"),
  sales: U("photo-1542744173-8e7e53415bb0"),
  chip: U("photo-1697577418970-95d99b5a55cf"),
  lab: U("photo-1778489769184-45868633c527"),
  workshop: U("photo-1622675363311-3e1904dc1885"),
};

const PROGRAMS = [
  { id: "ebyb", tag: "Sales Enablement", tint: "bg-amber-50 text-amber-700", title: "Explore Beyond Your Boundaries\u2122", desc: "Advanced selling skills and professional excellence for high-performing sales teams.", img: IMG.sales, to: "/programs#sales-transformation" },
  { id: "ai-catalyst", tag: "AI Certification", tint: "bg-sky-50 text-sky-700", title: "AI Catalyst\u2122", desc: "Demystifying AI through beginner-to-advanced programs built on practical, hands-on learning.", img: IMG.chip, to: "/ai-catalyst" },
  { id: "intel-unnati", tag: "AI Labs for Colleges", tint: "bg-teal-50 text-teal-700", title: "Intel\u00AE Unnati Partnership", desc: "World-class AI education and lab infrastructure for institutions, in collaboration with Intel.", img: IMG.lab, to: "/programs#intel-unnati" },
];

const PRINCIPLE_TINTS = ["bg-sky-50 border-sky-100", "bg-amber-50 border-amber-100", "bg-teal-50 border-teal-100"];

const Eyebrow = ({ children, dark = false }) => (
  <p className={`text-xs font-semibold uppercase tracking-[0.25em] mb-4 flex items-center gap-3 ${dark ? "text-[#C9A962]" : "text-[#A8873E]"}`}>
    <span className="h-px w-8 bg-current opacity-60" />{children}
  </p>
);

const Hero = () => (
  <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#0A1628]" data-testid="home-hero">
    <motion.img
      src={IMG.hero}
      alt="Business professionals collaborating"
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 2.4, ease: "easeOut" }}
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/30" />
    <div className="container-custom relative z-10 py-20">
      <motion.div initial="initial" animate="animate" variants={staggerContainer} className="max-w-3xl">
        <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#C9A962]/15 border border-[#C9A962]/40 rounded-full px-4 py-1.5 mb-6">
          <Shield size={14} className="text-[#C9A962]" />
          <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">ISO/IEC 27001:2022 Certified</span>
        </motion.div>
        <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.08]">
          We Revolutionize Access to <span className="text-gradient">Ideas, Experts & Technologies</span>
        </motion.h1>
        <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#D0D6E0] mb-10 max-w-2xl">
          Turning Possibilities into Performance through Innovation and Expertise
        </motion.p>
        <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
          <Link to="/contact" className="btn-primary flex items-center gap-2" data-testid="hero-cta">
            Book a Consultation <ArrowRight size={18} />
          </Link>
          <Link to="/programs" className="btn-secondary flex items-center gap-2" data-testid="hero-secondary-cta">
            Explore Programs <ChevronRight size={18} />
          </Link>
        </motion.div>
      </motion.div>
    </div>
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.6 }}
      className="hidden lg:flex absolute bottom-12 right-8 xl:right-16 z-10 gap-3"
    >
      {[{ icon: Users, v: "1500+", l: "Professionals trained" }, { icon: Award, v: "125+", l: "Expert sessions" }].map((c) => (
        <div key={c.l} className="flex items-center gap-3 px-5 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/15">
          <c.icon size={20} className="text-[#C9A962]" />
          <div><p className="font-bold text-lg leading-none">{c.v}</p><p className="text-xs text-[#D0D6E0] mt-1">{c.l}</p></div>
        </div>
      ))}
    </motion.div>
  </section>
);

const Overview = () => (
  <section className="section bg-white text-[#0F172A]" data-testid="home-overview">
    <div className="container-custom">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div variants={fadeInUp}>
          <Eyebrow>Company Overview</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#0A1628]">Building Lasting Brands & Future-Ready Skills</h2>
          <p className="text-slate-600 text-lg mb-6 leading-relaxed">
            At <strong className="text-[#0A1628]">Qbic Catalyst Partners</strong>, we believe that progress lies at the intersection of knowledge, innovation, and opportunity. We collaborate with clients across the globe to build lasting brands, develop future-ready skills, and make artificial intelligence accessible to all.
          </p>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Driven by a mission to empower individuals and organizations, we offer strategic guidance, precise execution, and expert support to unlock their full potential. Whether through demystifying AI, fostering innovation, or nurturing leadership, Qbic Catalyst acts as a trusted partner on the journey to transformation.
          </p>
          <Link to="/about" className="text-[#A8873E] font-semibold flex items-center gap-2 hover:gap-4 transition-all" data-testid="home-about-link">
            Learn More About Us <ArrowRight size={18} />
          </Link>
        </motion.div>
        <motion.div variants={fadeInUp} className="relative">
          <div className="absolute -top-5 -right-5 w-2/3 h-2/3 rounded-2xl bg-amber-50 border border-amber-100" />
          <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
            <img src={IMG.campus} alt="Students learning in a computer lab" className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-5 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#C9A962]/15 rounded-full flex items-center justify-center"><Shield className="text-[#A8873E]" size={24} /></div>
              <div><p className="text-[#0A1628] font-semibold">ISO/IEC 27001:2022</p><p className="text-slate-500 text-sm">Certified Company</p></div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const Stats = () => (
  <section className="section bg-[#0A1628] relative overflow-hidden" data-testid="home-stats">
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
    <div className="container-custom relative">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="mb-14">
        <motion.div variants={fadeInUp}><Eyebrow dark>Our Impact</Eyebrow></motion.div>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold">Key Milestones & Achievements</motion.h2>
      </motion.div>
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="stats-grid">
        {HOME_STATS.map((stat) => <StatCard key={stat.id} end={stat.end} suffix={stat.suffix} label={stat.label} icon={stat.icon} />)}
      </motion.div>
    </div>
  </section>
);

const Principles = () => (
  <section className="section bg-[#F8FAFC] text-[#0F172A]" data-testid="home-principles">
    <div className="container-custom">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="mb-14 max-w-2xl">
        <motion.div variants={fadeInUp}><Eyebrow>Our Philosophy</Eyebrow></motion.div>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-[#0A1628]">Guiding Principles</motion.h2>
      </motion.div>
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="grid md:grid-cols-3 gap-6">
        {HOME_PRINCIPLES.map((p, i) => (
          <motion.div key={p.id} variants={fadeInUp} className={`p-8 rounded-2xl border ${PRINCIPLE_TINTS[i]} transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-xl`}>
            <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center mb-6"><p.icon className="text-[#A8873E]" size={26} /></div>
            <h3 className="text-xl font-bold mb-3 text-[#0A1628]">{p.title}</h3>
            <p className="text-slate-600 leading-relaxed">{p.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const Programs = () => (
  <section className="section bg-white text-[#0F172A]" data-testid="home-programs">
    <div className="container-custom">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <motion.div variants={fadeInUp}><Eyebrow>Featured</Eyebrow></motion.div>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-[#0A1628]">Programs & Services</motion.h2>
        </div>
        <motion.div variants={fadeInUp}>
          <Link to="/programs" className="text-[#A8873E] font-semibold flex items-center gap-2 hover:gap-4 transition-all" data-testid="home-all-programs-link">View all programs <ArrowRight size={18} /></Link>
        </motion.div>
      </motion.div>
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
        {PROGRAMS.map((p) => (
          <motion.div key={p.id} variants={fadeInUp} className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-slate-200 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-2xl" data-testid={`program-card-${p.id}`}>
            <div className="overflow-hidden aspect-[16/10]">
              <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
            <div className="p-7 flex flex-col flex-1">
              <span className={`self-start text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4 ${p.tint}`}>{p.tag}</span>
              <h3 className="text-xl font-bold mb-3 text-[#0A1628]">{p.title}</h3>
              <p className="text-slate-600 mb-6 flex-1">{p.desc}</p>
              <Link to={p.to} className="text-[#A8873E] font-semibold flex items-center gap-2 hover:gap-4 transition-all" data-testid={`program-link-${p.id}`}>
                Learn More <ChevronRight size={18} />
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="section bg-[#0A1628]" data-testid="home-why">
    <div className="container-custom">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div variants={fadeInUp} className="relative order-2 lg:order-1">
          <div className="overflow-hidden rounded-2xl group">
            <img src={IMG.workshop} alt="Corporate workshop in session" className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div className="absolute -bottom-6 right-6 bg-[#C9A962] text-[#0A1628] rounded-xl px-6 py-4 shadow-xl">
            <p className="text-2xl font-bold leading-none">13+</p><p className="text-xs font-medium mt-1">Enterprise clients</p>
          </div>
        </motion.div>
        <motion.div variants={fadeInUp} className="order-1 lg:order-2">
          <Eyebrow dark>Why Choose Us</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Your Trusted Partner for Transformation</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {WHY_CHOOSE_US.map((item) => (
              <div key={item.id} className="p-5 rounded-xl bg-[#121E30] border border-[#2A3648] transition-colors hover:border-[#C9A962]/40">
                <item.icon className="text-[#C9A962] mb-3" size={22} />
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const Cta = () => (
  <section className="section bg-[#FAF8F5] text-[#0F172A] border-t border-[#E8DFD1]" data-testid="home-cta">
    <div className="container-custom">
      <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
        <div>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#0A1628]">
            Ready to <span className="text-[#A8873E]">Transform</span>?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8 max-w-xl">
            Partner with Qbic Catalyst to unlock your organization's full potential and drive sustainable growth.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary flex items-center gap-2" data-testid="cta-consultation">
              Book a Consultation <ArrowRight size={18} />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 px-7 py-3 rounded border border-[#0A1628]/20 text-[#0A1628] font-semibold hover:bg-[#0A1628] hover:text-white transition-colors" data-testid="cta-services">
              Explore Services <ChevronRight size={18} />
            </Link>
          </motion.div>
        </div>
        <motion.ul variants={fadeInUp} className="space-y-3">
          {["Go-To-Market consulting for enterprises", "Digital transformation for MSMEs", "AI training & Intel Unnati labs"].map((t) => (
            <li key={t} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#E8DFD1]">
              <CheckCircle className="text-[#A8873E] flex-shrink-0" size={18} /><span className="text-[#0A1628] font-medium">{t}</span>
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <SEO
        title={null}
        description="Qbic Catalyst Partners empowers organizations through Go-To-Market Consulting, Digital Transformation, AI Training Programs, and Intel Partnerships. ISO/IEC 27001:2022 Certified. Turning Possibilities into Performance."
        keywords="consulting services, go-to-market strategy, digital transformation, AI training, Intel Unnati, sales transformation, enterprise consulting, business consulting"
        path="/"
      />
      <Hero />
      <PartnerMarquee />
      <Overview />
      <Stats />
      <Principles />
      <Programs />
      <WhyUs />
      <Cta />
    </>
  );
}
